import { SupportedDailyWeeklyRentalTypes } from 'enums/supportedRentalTypes';
import { FormAction } from 'hooks/useForm';
import { Dispatch, useEffect, useRef } from 'react';
import { dateService } from 'services/DateService';
import { VehicleBasedDailyWeeklyDemandState } from 'types/demand.type';

export function usePriceWidgetDWClosestAvailability({
  state,
  dispatch,
  mappedReservedTimes,
  getDisabledDays,
  disableDatesBeforeToday,
}: {
  state: VehicleBasedDailyWeeklyDemandState;
  dispatch: Dispatch<FormAction<VehicleBasedDailyWeeklyDemandState>>;
  mappedReservedTimes: Date[];
  getDisabledDays: (date: Date) => boolean;
  disableDatesBeforeToday: (date: Date) => boolean;
}) {
  const dayjs = dateService.getDayjs();
  const isFirstLoop = useRef<boolean>(true);

  const isDateAvailable = (date: Date) => {
    if (getDisabledDays(date)) return false;
    return !mappedReservedTimes.some(
      (d) =>
        dateService.getFormattedDate(d) === dateService.getFormattedDate(date)
    );
  };

  const isDateAvailableWeekly = (date: Date) => {
    if (disableDatesBeforeToday(date)) return false;
    return !mappedReservedTimes.some(
      (d) =>
        dateService.getFormattedDate(d) === dateService.getFormattedDate(date)
    );
  };

  const isDateRangeAvailable = ({
    startDate,
    endDate,
  }: {
    startDate: Date;
    endDate: Date;
  }) => {
    const dayDifference = dateService.getDayDuration(startDate, endDate);

    if (dayDifference < 0) return false;
    if (dayDifference === 0) return isDateAvailable(startDate);

    const dates = [...Array(dayDifference + 1)].map((_, idx) => {
      return dayjs(startDate)
        .add(+idx, 'days')
        .toDate();
    });

    for (const key in dates) {
      const date = dates[key];
      if (state.demandVariant === SupportedDailyWeeklyRentalTypes.WEEKLY) {
        if (+key === 0 || +key === dates.length - 1) {
          if (!isDateAvailable(date)) return false;
        } else {
          if (!isDateAvailableWeekly(date)) return false;
        }
      } else {
        if (!isDateAvailable(date)) return false;
      }
    }
    return true;
  };

  const findFirstAvailableDate = (
    startDate: Date,
    endDate: Date,
    demandVariant: SupportedDailyWeeklyRentalTypes
  ) => {
    let currentDayOffset = 0;
    const maxDayLimit = 100;
    const startEndOffset =
      demandVariant === SupportedDailyWeeklyRentalTypes.HALF_DAY
        ? 0
        : demandVariant === SupportedDailyWeeklyRentalTypes.DAILY
        ? 1
        : 7;
    const isEndDateAlignedWithOffset =
      dateService.getDayDuration(startDate, endDate) % startEndOffset === 0;
    while (currentDayOffset < maxDayLimit) {
      const currentStartDay = dayjs(startDate)
        .add(currentDayOffset, 'days')
        .toDate();
      const currentEndDay = (
        isFirstLoop.current && isEndDateAlignedWithOffset
          ? dayjs(endDate)
          : dayjs(currentStartDay).add(startEndOffset, 'days')
      ).toDate();
      isFirstLoop.current = false;

      if (
        isDateRangeAvailable({
          startDate: currentStartDay,
          endDate: currentEndDay,
        })
      ) {
        return {
          startDate: currentStartDay,
          endDate: currentEndDay,
        };
      }
      currentDayOffset++;
    }
  };

  useEffect(() => {
    if (state.startDate && state.endDate) {
      const findedFirstAvailableDate = findFirstAvailableDate(
        state.startDate,
        state.endDate,
        state.demandVariant
      );
      if (typeof findedFirstAvailableDate !== 'undefined') {
        const { startDate, endDate } = findedFirstAvailableDate;
        if (startDate !== state.startDate) {
          dispatch({
            type: 'startDate',
            payload: startDate,
          });
        }
        if (endDate !== state.endDate) {
          dispatch({
            type: 'endDate',
            payload: endDate,
          });
        }
      }
    }
  }, [state.demandVariant, mappedReservedTimes]);

  return {
    isDateAvailable,
    isDateRangeAvailable,
  };
}
